import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ChevronDown, Cpu, Download, Eye, Pencil, Plus, Server, Trash2, Wrench } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { ModelView } from "@/api/generated/ModelView"
import type { Provider } from "@/api/generated/Provider"
import { useDeleteModel, useModels, useProviders } from "@/api/queries/providers"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { formatCost, formatTokens } from "@/features/runs/format"
import { ImportModelsDialog } from "@/features/studio/import-models-dialog"
import { ModelSheet } from "@/features/studio/model-sheet"
import { useProviderKindLabel } from "@/features/studio/provider-kind"
import { formatNumber } from "@/lib/format"

interface ModelsSearch {
  provider?: number
}

export const Route = createFileRoute("/_app/studio/models")({
  staticData: { titleKey: "nav.items.models" },
  validateSearch: (s: Record<string, unknown>): ModelsSearch => ({
    provider: Number(s.provider) > 0 ? Number(s.provider) : undefined,
  }),
  component: ModelsPage,
})

function ModelsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const models = useModels()
  const providers = useProviders()
  const remove = useDeleteModel()
  const confirm = useConfirm()
  const [editing, setEditing] = useState<ModelView>()
  const [open, setOpen] = useState(false)
  const [importing, setImporting] = useState<Provider>()
  const kindLabel = useProviderKindLabel()

  const data = useMemo(
    () => (search.provider ? models.data?.filter((m) => m.provider_id === search.provider) : models.data),
    [models.data, search.provider],
  )

  const columns = useMemo(
    () =>
      createColumns<ModelView>((c) => [
        c.accessor("name", {
          header: t("studio.models.columns.model"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="min-w-0">
              <div className="truncate font-medium">{row.original.name}</div>
              <code className="text-muted-foreground block max-w-72 truncate font-mono text-xs">
                {row.original.model}
              </code>
            </div>
          ),
        }),
        c.accessor("provider_name", {
          header: t("studio.models.columns.provider"),
          cell: ({ row }) => (
            <div className="text-[0.8125rem]">
              <div>{row.original.provider_name}</div>
              <div className="text-muted-foreground text-xs">{kindLabel(row.original.provider_kind)}</div>
            </div>
          ),
        }),
        c.accessor((m) => m.context_length ?? 0, {
          id: "context",
          header: t("studio.models.columns.limits"),
          meta: { label: t("studio.models.columns.limits") },
          cell: ({ row }) => (
            <span className="text-muted-foreground text-xs tabular-nums">
              {t("studio.models.limits", {
                ctx: row.original.context_length ? formatTokens(row.original.context_length) : "—",
                output: row.original.max_output_tokens ? formatTokens(row.original.max_output_tokens) : "—",
              })}
            </span>
          ),
        }),
        c.display({
          id: "capabilities",
          header: t("studio.models.columns.capabilities"),
          meta: { label: t("studio.models.columns.capabilities") },
          cell: ({ row }) => (
            <div className="text-muted-foreground flex items-center gap-2">
              {row.original.supports_vision && (
                <Tooltip>
                  <TooltipTrigger render={<span />}>
                    <Eye className="size-3.5" aria-label={t("studio.models.vision")} />
                  </TooltipTrigger>
                  <TooltipContent>{t("studio.models.vision")}</TooltipContent>
                </Tooltip>
              )}
              {row.original.supports_tools && (
                <Tooltip>
                  <TooltipTrigger render={<span />}>
                    <Wrench className="size-3.5" aria-label={t("studio.models.tools")} />
                  </TooltipTrigger>
                  <TooltipContent>{t("studio.models.tools")}</TooltipContent>
                </Tooltip>
              )}
              {row.original.fallback_model_name && (
                <StatusBadge className="max-w-36 truncate">
                  {t("studio.models.fallbackShort", { name: row.original.fallback_model_name })}
                </StatusBadge>
              )}
            </div>
          ),
        }),
        c.accessor((m) => m.input_per_1m ?? -1, {
          id: "price",
          header: t("studio.models.columns.price"),
          meta: { label: t("studio.models.columns.price"), align: "end" },
          cell: ({ row }) => {
            const m = row.original
            if (m.input_per_1m == null || m.output_per_1m == null)
              return <span className="text-muted-foreground text-xs">{t("studio.models.noPrice")}</span>
            return (
              <span className="text-xs whitespace-nowrap tabular-nums">
                {formatCost(m.input_per_1m, m.currency ?? "USD")}{" "}
                <span className="text-muted-foreground">/</span>{" "}
                {formatCost(m.output_per_1m, m.currency ?? "USD")}
              </span>
            )
          },
        }),
        c.accessor("agent_count", {
          header: t("studio.models.columns.agents"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
      ]),
    [t, kindLabel],
  )

  const confirmDelete = (m: ModelView) =>
    confirm({
      title: t("studio.models.deleteTitle", { name: m.name }),
      description:
        m.agent_count > 0
          ? t("studio.models.deleteInUse", { count: m.agent_count })
          : t("studio.models.deleteDescription"),
      confirmLabel: t("studio.models.delete"),
      destructive: true,
      onConfirm: () => remove.mutateAsync(m),
    })

  const noProviders = providers.isSuccess && providers.data.length === 0

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.models.title")}
        description={t("studio.models.description")}
        icon={Cpu}
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" disabled={noProviders} />}>
                <Download />
                {t("studio.models.importFrom")}
                <ChevronDown />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-auto min-w-48">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>{t("studio.models.chooseProvider")}</DropdownMenuLabel>
                  {providers.data?.map((p) => (
                    <DropdownMenuItem key={p.id} onClick={() => setImporting(p)}>
                      <Server />
                      {p.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={() => {
                setEditing(undefined)
                setOpen(true)
              }}
              disabled={noProviders}
              data-testid="new-model"
            >
              <Plus />
              {t("studio.models.new")}
            </Button>
          </>
        }
      />
      {noProviders ? (
        <EmptyState
          icon={Server}
          title={t("studio.models.noProviders.title")}
          description={t("studio.models.noProviders.description")}
          action={
            <Button
              size="sm"
              nativeButton={false}
              render={<Link to="/studio/providers" search={{ new: true }} />}
            >
              <Plus />
              {t("studio.providers.new")}
            </Button>
          }
        />
      ) : (
        <DataTable
          label={t("studio.models.title")}
          data={data}
          columns={columns}
          getRowId={(m) => String(m.id)}
          loading={models.isPending}
          error={models.error}
          onRetry={() => void models.refetch()}
          searchable
          searchPlaceholder={t("studio.models.search")}
          initialSorting={[{ id: "name", desc: false }]}
          columnToggle
          persistKey="studio-models"
          onRowClick={(m) => {
            setEditing(m)
            setOpen(true)
          }}
          toolbar={
            (providers.data?.length ?? 0) > 1 && (
              <Select
                value={search.provider != null ? String(search.provider) : "all"}
                onValueChange={(v) =>
                  void navigate({
                    search: { provider: v === "all" || v == null ? undefined : Number(v) },
                    replace: true,
                  })
                }
              >
                <SelectTrigger
                  size="sm"
                  className="h-8 w-44"
                  aria-label={t("studio.models.columns.provider")}
                >
                  <SelectValue>
                    {(v: string) =>
                      v === "all"
                        ? t("studio.models.allProviders")
                        : (providers.data?.find((p) => String(p.id) === v)?.name ?? v)
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("studio.models.allProviders")}</SelectItem>
                  {providers.data?.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          }
          rowActions={(m) => (
            <>
              <DropdownMenuItem
                onClick={() => {
                  setEditing(m)
                  setOpen(true)
                }}
              >
                <Pencil />
                {t("common.edit")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={() => void confirmDelete(m)}>
                <Trash2 />
                {t("studio.models.delete")}
              </DropdownMenuItem>
            </>
          )}
          emptyState={
            <EmptyState
              variant="plain"
              icon={Cpu}
              title={t("studio.models.empty.title")}
              description={t("studio.models.empty.description")}
              action={
                <Button size="sm" onClick={() => setOpen(true)}>
                  <Plus />
                  {t("studio.models.new")}
                </Button>
              }
            />
          }
        />
      )}
      <ModelSheet
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (!o) setEditing(undefined)
        }}
        model={editing}
      />
      <ImportModelsDialog provider={importing} onOpenChange={(o) => !o && setImporting(undefined)} />
    </PageContainer>
  )
}
